import React, { useState, useEffect } from 'react';
import { X, User, Mail, Calendar, AlertTriangle, Shield, Briefcase } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const UserDetailModal = ({ user, isOpen, onClose }) => {
  const { dashboardData } = useDashboard();
  const [userProjects, setUserProjects] = useState([]);
  const [userProjectCount, setUserProjectCount] = useState(0);

  // Get user's billable projects from time entries data and calculate project count
  useEffect(() => {
    if (user && dashboardData?.details?.userUtilization && dashboardData?.details?.timeEntries) {
      const userId = user.userId;
      const userTimeEntries = dashboardData.details.timeEntries.filter(entry => 
        entry.user?.userId === userId && entry.billable
      );
      
      // Group by project and calculate billable hours
      const projectMap = new Map();
      userTimeEntries.forEach(entry => {
        const projectName = entry.project?.projectName || 'Unknown Project';
        const minutes = entry.minutes || 0;
        
        if (projectMap.has(projectName)) {
          projectMap.set(projectName, projectMap.get(projectName) + minutes);
        } else {
          projectMap.set(projectName, minutes);
        }
      });
      
      // Convert to array and sort by billable hours (descending)
      const projects = Array.from(projectMap.entries())
        .map(([projectName, minutes]) => ({
          projectName,
          billableHours: Math.round((minutes / 60) * 100) / 100
        }))
        .sort((a, b) => b.billableHours - a.billableHours)
        .slice(0, 15); // Limit to 15 projects
      
      setUserProjects(projects);
      setUserProjectCount(projects.length);
    }

    // Calculate project count from projects data using teamMembers
    if (user && dashboardData?.details?.projects) {
      const userId = user.userId;
      let count = 0;
      
      dashboardData.details.projects.forEach(project => {
        if (project.teamMembers?.members && Array.isArray(project.teamMembers.members)) {
          const isMember = project.teamMembers.members.some(member => member.userId === userId);
          if (isMember) {
            count++;
          }
        }
      });
      
      setUserProjectCount(count);
    }
  }, [user, dashboardData]);

  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format createdBy name
  const formatCreatedBy = (createdBy) => {
    if (!createdBy) return 'System';
    
    // If createdBy is an object with firstName and lastName
    if (typeof createdBy === 'object' && createdBy.firstName && createdBy.lastName) {
      return `${createdBy.firstName} ${createdBy.lastName}`;
    }
    
    // If createdBy is a string, return as is
    if (typeof createdBy === 'string') {
      return createdBy;
    }
    
    return 'System';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <User size={20} />
            <h3>User Details</h3>
          </div>
          {/* Close button moved inside the detail container */}
          <button onClick={onClose} className="modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          {/* User Profile Section with enhanced styling */}
          <div className="user-profile-section">
            <div className="user-profile">
              <div className="user-avatar">
                {user.profilePictureUrl ? (
                  <img 
                    src={user.profilePictureUrl} 
                    alt={`${user.name || 'User'}'s profile picture`}
                    className="user-profile-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <User size={48} style={{ display: user.profilePictureUrl ? 'none' : 'block' }} />
              </div>
              <div className="user-info">
                <h4 className="user-name">{user.name}</h4>
                <p className="user-role">{user.role}</p>
              </div>
            </div>
          </div>

          <div className="user-details-grid">
            <div className="detail-item">
              <div className="detail-label">
                <Mail size={16} />
                <span>Email</span>
              </div>
              <div className="detail-value">{user.email || 'Not available'}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Shield size={16} />
                <span>Role</span>
              </div>
              <div className="detail-value">{user.role || 'Member'}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Calendar size={16} />
                <span>Created</span>
              </div>
              <div className="detail-value">{formatDate(user.createdAt)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <User size={16} />
                <span>Created By</span>
              </div>
              <div className="detail-value">{formatCreatedBy(user.createdBy)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Calendar size={16} />
                <span>Last Updated</span>
              </div>
              <div className="detail-value">{formatDate(user.updatedAt)}</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Shield size={16} />
                <span>Status</span>
              </div>
              <div className="detail-value">
                <span className={`status-badge ${user.status?.toLowerCase() || 'active'}`}>
                  {user.status || 'Active'}
                </span>
              </div>
            </div>
          </div>

          <div className="user-stats">
            <h5>Performance Statistics</h5>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{user.utilization?.toFixed(1) || 0}%</div>
                <div className="stat-label">Utilization</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{user.billableHours?.toFixed(1) || 0}h</div>
                <div className="stat-label">Billable Hours</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{user.totalHours?.toFixed(1) || 0}h</div>
                <div className="stat-label">Total Hours</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{user.workWeekHours || 40}h</div>
                <div className="stat-label">Capacity</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{userProjectCount}</div>
                <div className="stat-label">Projects</div>
              </div>
            </div>
          </div>

          {/* Billable Projects Table */}
          <div className="user-billable-projects">
            <h5>
              <Briefcase size={16} />
              Billable Projects
            </h5>
            {userProjects.length > 0 ? (
              <div className="projects-table-container">
                <table className="projects-table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Billable Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userProjects.map((project, index) => (
                      <tr key={index}>
                        <td className="project-name-cell">{project.projectName}</td>
                        <td className="billable-hours-cell">{project.billableHours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-projects-message">No billable projects found for this user.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
